import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, ArrowDownWideNarrow } from 'lucide-react'
import { useMessageScroller, useMessageScrollerScrollable } from '@shadcn/react/message-scroller'
import type { ChatListItem } from './ChatMessageList'

export type NavUserMessage = { id: string; content: string; index: number }

// Presentational bar for stepping through user turns and jumping back to the live edge. Adapted from
// replayio/nut.new MessageNavigator; styled for loop-qa's chat chrome.
export function MessageNavigator({
  userMessages,
  currentIndex,
  onNavigate,
  onScrollToBottom,
  showJumpToBottom,
}: {
  userMessages: NavUserMessage[]
  currentIndex: number
  onNavigate: (index: number) => void
  onScrollToBottom: () => void
  showJumpToBottom: boolean
}) {
  if (userMessages.length === 0) return null

  const currentMessage = userMessages[currentIndex]
  if (!currentMessage) return null

  const canGoBack = currentIndex > 0
  const canGoForward = currentIndex < userMessages.length - 1

  return (
    <div className="chat-navigator" data-testid="chat-message-navigator">
      <div className="chat-navigator__text" title={currentMessage.content}>
        {currentMessage.content}
      </div>
      <div className="chat-navigator__controls">
        <button
          type="button"
          className="chat-navigator__btn"
          onClick={() => canGoBack && onNavigate(currentIndex - 1)}
          disabled={!canGoBack}
          aria-label="Previous message"
          data-testid="chat-navigator-prev"
        >
          <ChevronLeft size={16} aria-hidden />
        </button>
        <button
          type="button"
          className="chat-navigator__btn"
          onClick={() => canGoForward && onNavigate(currentIndex + 1)}
          disabled={!canGoForward}
          aria-label="Next message"
          data-testid="chat-navigator-next"
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>
      <div className="chat-navigator__sep" aria-hidden />
      <button
        type="button"
        className="chat-navigator__btn"
        onClick={onScrollToBottom}
        disabled={!showJumpToBottom}
        aria-label="Scroll to latest"
        data-testid="chat-navigator-bottom"
      >
        <ArrowDownWideNarrow size={16} aria-hidden />
      </button>
    </div>
  )
}

// Connected navigator: derives user messages from the transcript and scrolls via MessageScroller.
// `historyLoading` is the transcript's first fetch: the bar can only be built once the user turns are
// known, and inserting it above the log afterwards pushes the whole transcript down — so while the
// fetch is out its slot is held open instead. See the placeholder branch below.
export function ChatMessageNavigator({ items, historyLoading }: { items: ChatListItem[]; historyLoading?: boolean }) {
  const { scrollToEnd, scrollToMessage } = useMessageScroller()
  const scrollable = useMessageScrollerScrollable()
  const [currentIndex, setCurrentIndex] = useState(-1)

  const userMessages = useMemo<NavUserMessage[]>(() => (
    items
      .filter((it): it is ChatListItem & { message: NonNullable<ChatListItem['message']> } =>
        !!it.message && it.message.role === 'user')
      .map((it, index) => ({
        id: it.id,
        content: it.message.content.trim() || 'Message',
        index,
      }))
  ), [items])

  // Start on the latest user turn once messages exist.
  useEffect(() => {
    if (userMessages.length > 0 && currentIndex === -1) {
      setCurrentIndex(userMessages.length - 1)
    }
  }, [userMessages.length, currentIndex])

  // When the user sends a new message, follow it to the live edge.
  const lastUserId = userMessages[userMessages.length - 1]?.id
  useEffect(() => {
    if (!lastUserId) return
    setCurrentIndex(userMessages.length - 1)
  }, [lastUserId, userMessages.length])

  const handleNavigate = useCallback((index: number) => {
    const target = userMessages[index]
    if (!target) return
    setCurrentIndex(index)
    scrollToMessage(target.id, { behavior: 'smooth', align: 'start' })
  }, [userMessages, scrollToMessage])

  const handleScrollToBottom = useCallback(() => {
    scrollToEnd({ behavior: 'smooth' })
    if (userMessages.length > 0) setCurrentIndex(userMessages.length - 1)
  }, [scrollToEnd, userMessages.length])

  // An empty bar rather than nothing, for as long as the turns it lists might still be coming. Once
  // the fetch lands with no user turns (a chat nobody has spoken in yet) it collapses for real.
  if (userMessages.length === 0) {
    return historyLoading ? <div className="chat-navigator chat-navigator--placeholder" aria-hidden /> : null
  }

  const resolvedIndex = currentIndex >= 0 ? currentIndex : userMessages.length - 1

  return (
    <MessageNavigator
      userMessages={userMessages}
      currentIndex={resolvedIndex}
      onNavigate={handleNavigate}
      onScrollToBottom={handleScrollToBottom}
      showJumpToBottom={scrollable.end}
    />
  )
}
